import React, { useState } from "react";
import {
  Anchor,
  Box,
  Button,
  Divider,
  Flex,
  Group,
  Loader,
  NavLink,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { FiArrowRight, FiSearch } from "react-icons/fi";
import { Link } from "react-router-dom";
import { useGetMultiplacerProjectsQuery } from "../openapi";
import { useAppSelector } from "./app/hooks";
import { selectAvailableProjectsForUser } from "./projectSelectors";
import { isProjectViewerOnlyProject } from "./projectViewerOnlyProject";

export const Projects: React.FC = () => {
  const { isError, isLoading, refetch } = useGetMultiplacerProjectsQuery(undefined);
  const projects = useAppSelector(selectAvailableProjectsForUser);
  const [search, setSearch] = useState("");
  const query = search.trim().toLocaleLowerCase("nb");
  const visibleProjects = projects.filter((project) =>
    `${project.title} ${project.projectNo}`.toLocaleLowerCase("nb").includes(query)
  );

  return (
    <Box w="100%" h="100%" mih={0} p={{ base: "md", sm: "xl" }} style={{ overflow: "auto" }}>
      <Paper
        component="section"
        aria-labelledby="projects-title"
        withBorder
        radius="md"
        w="100%"
        maw={960}
        mah="100%"
        mx="auto"
        style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        <Stack component="header" gap={6} p={{ base: "md", sm: "xl" }} style={{ flexShrink: 0 }}>
          <Title id="projects-title" order={1} size="h2">
            Velg prosjekt
          </Title>
          <Text size="sm" c="dimmed">
            Åpne et prosjekt for å fortsette arbeidet.
          </Text>
        </Stack>

        {!isLoading && !isError && projects.length > 0 && (
          <Box px={{ base: "md", sm: "xl" }} pb="lg" style={{ flexShrink: 0 }}>
            <TextInput
              aria-label="Søk etter prosjekt eller prosjektnummer"
              placeholder="Søk etter prosjekt eller prosjektnummer…"
              leftSection={<FiSearch size={16} />}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              size="sm"
            />
          </Box>
        )}

        <Divider />
        <Box mih={0} style={{ overflow: "auto" }}>
          {isLoading ? (
            <Stack p="xl" gap="xs" role="status">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Henter prosjekter…
              </Text>
            </Stack>
          ) : isError ? (
            <Stack p="xl" gap="xs" align="flex-start" role="alert">
              <Title order={2} size="h5">
                Klarte ikke å hente prosjekter
              </Title>
              <Button variant="subtle" onClick={() => refetch()}>
                Prøv igjen
              </Button>
            </Stack>
          ) : projects.length === 0 ? (
            <Stack p="xl" gap="xs">
              <Title order={2} size="h5">
                Ingen tilgang til prosjekter
              </Title>
              <Text size="sm" c="dimmed">
                Ta kontakt med en prosjektadmin hvis du mangler tilgang til et prosjekt.
              </Text>
            </Stack>
          ) : (
            <>
              <Group px={{ base: "md", sm: "xl" }} py="md" gap="lg" wrap="nowrap">
                <Text size="xs" c="dimmed" fw={600} flex={1}>
                  Prosjekter{" "}
                  <Text span ml="xs" fw={400}>
                    {visibleProjects.length}
                  </Text>
                </Text>
                <Text size="xs" c="dimmed" fw={600} w={120} mr={32} visibleFrom="sm">
                  Prosjektnr.
                </Text>
              </Group>
              {visibleProjects.length === 0 ? (
                <Stack p="xl" gap="xs" align="flex-start" role="status">
                  <Title order={2} size="h5">
                    Ingen prosjekter matcher søket
                  </Title>
                  <Text size="sm" c="dimmed">
                    Prøv et annet navn eller prosjektnummer.
                  </Text>
                  <Button variant="subtle" onClick={() => setSearch("")}>
                    Tøm søk
                  </Button>
                </Stack>
              ) : (
                <Stack
                  component="ul"
                  aria-label="Tilgjengelige prosjekter"
                  gap={0}
                  m={0}
                  px={{ base: "xs", sm: "md" }}
                  pb="md"
                  style={{ listStyle: "none" }}
                >
                  {visibleProjects.map((project) => (
                    <Box component="li" key={project.projectNo}>
                      <Divider />
                      <NavLink
                        component={Link}
                        to={`/${project.projectNo}/${isProjectViewerOnlyProject(project.projectNo) ? "viewer" : "modules"}`}
                        px={{ base: "xs", sm: "md" }}
                        py="lg"
                        rightSection={<FiArrowRight size={17} aria-hidden="true" />}
                        label={
                          <Flex
                            direction={{ base: "column", sm: "row" }}
                            align={{ base: "stretch", sm: "center" }}
                            gap={{ base: 4, sm: "lg" }}
                          >
                            <Text size="sm" fw={500} flex={1} style={{ overflowWrap: "anywhere" }}>
                              {project.title}
                            </Text>
                            <Text
                              size="sm"
                              c="dimmed"
                              w={{ base: "auto", sm: 120 }}
                              style={{ fontVariantNumeric: "tabular-nums", flexShrink: 0 }}
                            >
                              {project.projectNo}
                            </Text>
                          </Flex>
                        }
                      />
                    </Box>
                  ))}
                </Stack>
              )}
            </>
          )}
        </Box>

        <Divider />
        <Group
          component="footer"
          gap="xs"
          px={{ base: "md", sm: "xl" }}
          py="lg"
          style={{ flexShrink: 0 }}
        >
          <Text size="xs" c="dimmed">
            Trenger du et nytt prosjekt?
          </Text>
          <Anchor href="mailto:per.strom@multiconsult.no" size="xs">
            <Group component="span" gap={6}>
              Kontakt Per Strom <FiArrowRight size={14} aria-hidden="true" />
            </Group>
          </Anchor>
        </Group>
      </Paper>
    </Box>
  );
};
